import { isFeed7Id, feed7EventUrl } from '../lib/feed7';
import { proxyAsk, syncProxyDesk } from './proxyDesk';
import { useBookOpening, type OpenState } from './bookOpening';
import { feedKey, RU_FEED, PRIVATE_MIRROR, isCupisForkId } from '../lib/feedContext';
import { API_BASE } from '../lib/build';
import { intentFor, intentOf, withIntent, withoutIntent } from '../confirm/intent';
/**
 * Вход в вилку одним действием: открыть контору, положить сумму в буфер,
 * отметить поставленную ногу.
 *
 * Раньше это жило в двух местах — в кнопках карточки и в витрине, — и жило
 * по-разному: витрина не запоминала обещанную прибыль, и спасатель на её
 * попытках молчал. Такие расхождения не видны глазом и обнаруживаются деньгами,
 * поэтому вход собран сюда, а карточка, витрина и клавиатура зовут одно и то же.
 *
 * Здесь же и порядок дел внутри нажатия, который нельзя переставлять:
 *
 * 1. **Буфер первым.** Обращение к нему тратит пользовательский жест, а вкладка,
 *    открытая раньше, уводит фокус со страницы до того, как копирование началось.
 * 2. **Обещание раньше вкладки.** Подтверждатель обязан знать наш кэф до того,
 *    как чужая страница отрисуется (`confirm/publish.ts`).
 * 3. **Адрес — из заранее добытого входа.** Спрашивать сервер внутри нажатия
 *    нельзя: вкладку, открытую после ожидания сети, браузер блокирует (`entry.ts`).
 * 4. **Слепок после обоих окон.** `fetch` даже без `await` сжигает жест:
 *    первое окно ещё успевает, второе Edge кладёт вкладкой в сканер.
 * 6. **Второе окно — из первого.** Chrome из одного клика второе
 *    `window.open` сканера кладёт вкладкой в FORQ. `host.open` — нет.
 * 5. **Стол AdsPower включён — кассы только в профилях.** Каждая нога — в
 *    профиле своей конторы через Local API и CDP, панель вилки — окном здесь,
 *    в жесте. AdsPower молчит — красная строка, не Edge (решение 0012).
 */

import { Decimal } from 'decimal.js';

import { openTabsEnvelope } from '../confirm/openTabs';
import { type OpenPane, extPlacesPanes, openPanesEnvelope, parseOpenPanesOk, parseOpenPanesWait, versionAtLeast } from '../confirm/openPanes';
import { ADS_COPY, DESK_BOOKS_MIN, type BookLeg, bookKey, deskOwnsClick, deskSlots, profileName, useDesk } from './desk';
import { venueName } from './meta';
import { deskArea, deskPanel, placeDeskPanel } from './deskPanel';
import { pingExt } from '../confirm/ping';
import { promise, promiseOnServer, revokePromises, stakePush } from '../confirm/publish';
import { STAKE_PUSH } from '../confirm/stakePush';
import { needsIntentUpdate } from '../confirm/intentSupport';
import { eventHref, liveEventUrl, sameEvent } from '../drive/predictfun';
import { selEid } from '../drive/csgopositive';
import { buildRow, type RowLeg } from '../lib/row';
import { remember, type SightPriced } from '../lib/sight';
import { amountParam, couponUrl } from '../poly/coupon';
import { polyMarketAllowed } from '../poly/gate';
import { type DeskLayout, type SplitAxis, loadLayout } from './deskLayout';
import { capsInput, navigationCost, tempoOf, useSettings } from '../store/settings';
import { useAccounts } from './accounts';
import { entryLeg } from './entry';
import { copy } from './Grab';
import { MELLSTROY_SECTION, applyDoor, bcgameDoor, canonBook, cloudbetEventUrl, doorHome, eventDoor, isPinnacleStorefront, onexDoorUrl, overlayUrl, parseDoor, resolvePinnacleDoorUrl, thunderpickEventUrl, urlFor } from './hosts';
import { PAPER_UI, markPaper, usePaper } from './paper';
import { forkHref } from './route';
import { paintLiveAsks } from './liveBook';
import { holdLegs, isPolymarket } from './order';
import type { FeedRow } from './rows';
import { rememberFeedPlan } from '../book/cash/feedPlan';
import { forForm as formOf, formStake, isRubStake } from './show';
import { STILL_LIVE_BY_SOURCE, stillDeadSeconds } from './still';
import { blankStep, useScreen } from './store';

/** Рублёвая нога: гостевой купон, без денежного клика. */
const CUPIS_COUPON = new Set([
  'fonbet',
  'winline',
  'leon',
  'olimp',
  'zenit',
  'betboom',
  'bingoboom',
  'onexbetru',
]);

/** На «Русских» клик готовит купон ЦУПИС, не копирует только сумму. */
export function guestCupis(source: string): boolean {
  return RU_FEED && CUPIS_COUPON.has(source);
}

export type { SplitAxis };

function fxOf(): number {
  return useSettings.getState().fx;
}

function metaOf() {
  return useScreen.getState().meta;
}

/** Ноги в том порядке, в котором их берут. Открытая карточка держит расклад клика. */
export const legsOf = (row: FeedRow): RowLeg[] =>
  holdLegs(row.row.legs, useScreen.getState().seatLock[row.id]);

/** Числа строки так, как их видел человек. Строки, не float. */
export function pricedOf(row: FeedRow): SightPriced {
  return {
    profit: String(row.row.profit),
    staked: String(row.row.staked),
    payout: String(row.row.payout),
    roiPct: String(row.row.roiPct),
  };
}

/**
 * Что вводят в форму конторы. У ноги «против» это ставка соперника, а не риск;
 * без знака и пробелов, иначе поле суммы не примет строку. Валюта — площадки,
 * не банка: рубли у ру-конторы, доллары у зарубежной, шеры у биржи.
 */
export function forForm(leg: RowLeg): string {
  return formOf(leg, fxOf(), metaOf());
}

/**
 * Тумблер «рынок» на пилоте. Стакан и цена смерти сюда не входят:
 * на первом кадре их часто ещё нет, а купон уже должен быть Market.
 */
export function wantsMarket(leg: RowLeg): boolean {
  return isPolymarket(leg.source) && leg.unit === 'share'
    && polyMarketAllowed() && useSettings.getState().polyOrder === 'market';
}

/** Доллары в поле Market. Нет среднего по стакану — сумма с карточки. */
export function marketStake(leg: RowLeg): string | null {
  if (!wantsMarket(leg)) return null;
  return amountParam(leg.marketUsd ?? leg.usd ?? 0);
}

/**
 * Потолок для драйвера. Без `marketDead` extras всё равно несут Market:
 * URL и обещание собираются по `wantsMarket`, не отсюда.
 */
export function marketBits(leg: RowLeg): { order: 'market'; marketUsd: string; dead: string } | null {
  const usd = marketStake(leg);
  if (usd === null || leg.marketDead === undefined) return null;
  return { order: 'market', marketUsd: usd, dead: leg.marketDead };
}

/** Что класть в буфер: по рынку на сайте вводят доллары, лимиткой — шары. */
export function pasteOf(leg: RowLeg): string {
  return marketStake(leg) ?? forForm(leg);
}

/**
 * Площадки без входа на событие. Человек ищет сам — в буфер уходит название.
 *
 * Kalshi сюда не входит: владелец 24.08 проверил — открывается нужная страница.
 * GGBet в `VENUES` — event на `/match/`. Thunderpick может быть `section`,
 * но адаптер собирает матч — название не подменяем.
 */
const SEARCH_DOOR = new Set([
  'predictfun',
  'csgopositive',
  'sportsbet',
  '1win',
  'onewin',
  'welstroy',
]);

/**
 * Каталог, не матч: путь, который адаптер кладёт, когда слага события нет.
 * Глубже (`/sports/cs2/…`) — уже страница, не хаб.
 */
const CATALOG = new Set([
  '',
  '/',
  '/sports',
  '/sportsbook',
  '/betting/esports',
  '/bets',
  '/line',
  '/live',
  '/sport',
  '/esports',
  '/su/live',
  '/su/sport',
  '/ru/live',
  '/en/sports',
]);

function catalogUrl(url: string | null | undefined): boolean {
  if (typeof url !== 'string' || url === '') return false;
  try {
    const parsed = new URL(url);
    // Softswiss / Betby: хаб `/sport`, матч — `?bt-path=/{sport}/…`.
    const bt = parsed.searchParams.get('bt-path') ?? '';
    if (bt.startsWith('/') && bt.split('/').filter(Boolean).length >= 2) {
      return false;
    }
    const path = parsed.pathname.replace(/\/+$/, '') || '/';
    return CATALOG.has(path);
  } catch {
    return false;
  }
}

export function searchDoor(source: string, url?: string | null): boolean {
  // 1win-line native doors contain both the provider slug and numeric event ID.
  // A section URL still needs manual search; a match URL must keep its identity.
  if (['1win', 'welstroy'].includes(canonBook(source)) && url) {
    const owned = urlFor(source, url);
    if (owned && /^\/betting\/match\/sport\/[a-z0-9_-]+-\d+\/?$/i.test(new URL(owned).pathname)) return false;
  }
  // Predict.fun `/market/{slug}` is the event itself: the coupon wants the amount, not a query.
  if (source === 'predictfun' && url && eventHref(url)) return false;
  return SEARCH_DOOR.has(source) || catalogUrl(url);
}

/** В поиск конторы — без тире, как `Grab` названия в карточке и витрине. */
export function matchQuery(row: FeedRow): string {
  const door = row.row.fork.legs?.find((one) => searchDoor(one.s, one.url));
  const parts = door?.participants ?? row.row.fork.legs?.[0]?.participants ?? [];
  return parts.length > 0 ? parts.join(' ') : row.id;
}

/** Что класть в буфер на входе: название, если открываем дверь без события. */
export function bufferOf(row: FeedRow, leg: RowLeg, opened: readonly RowLeg[]): string {
  const openedDoor = opened.some((one) => searchDoor(one.source, urlOf(row.id, one)));
  if (openedDoor || searchDoor(leg.source, urlOf(row.id, leg))) {
    return matchQuery(row);
  }
  return pasteOf(leg);
}

/**
 * Адрес ноги: точный, если адресация успела ответить, иначе — из ленты.
 *
 * Чужой хост отводим: точнее — не значит «этой площадки». Купон или адресация
 * с fon.bet у ноги Леона не должны выигрывать у leon.ru из ленты, и тем более
 * не должны открываться вместо него.
 */
function eventIdOf(id: string, leg: RowLeg): string | null {
  if (leg.eid && /^\d+$/.test(leg.eid)) return leg.eid;
  const sel = typeof leg.sel === 'string' ? leg.sel.split('~')[0] : '';
  if (sel && /^\d+$/.test(sel)) return sel;
  const fork = useScreen.getState().forks.get(id) ?? useScreen.getState().hiddenForks.get(id)?.fork;
  const raw = fork?.legs?.find((one) => one.s === leg.source);
  const wire = typeof raw?.eid === 'string' ? raw.eid.trim() : '';
  return /^\d+$/.test(wire) ? wire : null;
}

function thunderpickFromMate(eid: string): string | null {
  const screen = useScreen.getState();
  const forks = [...screen.forks.values(), ...[...screen.hiddenForks.values()].map((one) => one.fork)];
  for (const fork of forks) {
    for (const mate of fork?.legs ?? []) {
      if (mate.s !== 'thunderpick') continue;
      const raw = mate.betUrl ?? mate.url;
      if (typeof raw !== 'string' || !thunderpickEventUrl(raw)) continue;
      try {
        const tail = new URL(raw).pathname.split('/').filter(Boolean).at(-1);
        if (tail === eid) return raw;
      } catch {
        continue;
      }
    }
  }
  return null;
}

export function urlOf(id: string, leg: RowLeg): string | null {
  const warmed = entryLeg(id, leg.source);
  const eid = eventIdOf(id, leg);
  let incoming = warmed?.url ?? leg.url;
  if (canonBook(leg.source) === 'thunderpick' && eid !== null) {
    const have = typeof incoming === 'string' && thunderpickEventUrl(incoming);
    const mate = have ? incoming : thunderpickFromMate(eid);
    if (mate) incoming = mate;
  }
  const candidate = eventDoor(leg.source, incoming, eid);
  const ownFeed7Brand = isFeed7Id(id) && (leg.source === '1xbet' || leg.source === 'leon');
  const primary = ownFeed7Brand ? feed7EventUrl(leg.source, incoming) : urlFor(leg.source, candidate) ?? urlFor(leg.source, warmed?.url) ?? urlFor(leg.source, incoming);
  const door = useAccounts.getState().accounts[leg.source]?.door;
  const doored = applyDoor(primary, door);
  const raw = parseDoor(door ?? '')
    ? doored
    : (overlayUrl(leg.source, primary, useAccounts.getState().accounts) ?? doored);
  if (raw === null) return null;
  const family = isFeed7Id(id) && leg.source === '1xbet' ? raw : canonBook(leg.source) === 'onexbet' ? onexDoorUrl(raw) : raw;
  const page = leg.source === 'predictfun'
    ? liveEventUrl(family)
    : canonBook(leg.source) === 'welstroy' && catalogUrl(family) ? MELLSTROY_SECTION : family;
  const opened = leg.source === 'bcgame'
    ? (bcgameDoor(page) === 'https://bc.game/sports' ? (bcgameFromMate(id) ?? 'https://bc.game/sports') : bcgameDoor(page))
    : page;
  if (opened === null) return null;
  const marked = leg.source === 'csgopositive' ? positiveHref(opened, leg.sel) : opened;
  // Limit дописываем здесь, не в общей ссылке ленты: шары зависят от банка.
  const order = wantsMarket(leg) ? 'market' : 'limit';
  const stake = marketStake(leg);
  return couponUrl(
    marked,
    order === 'market' && stake !== null ? { ...leg, marketUsd: Number(stake) } : leg,
    order,
  );
}

/** Id матча в hash: площадка его не читает, водитель находит строку без поиска. */
function positiveHref(url: string, sel?: string | null): string {
  const eid = selEid(sel ?? undefined);
  if (!eid) return url;
  try {
    const next = new URL(url);
    if (next.searchParams.get('e') || /(?:^|[&#])e=\d+/.test(next.hash)) return url;
    next.hash = `e=${eid}`;
    return next.toString();
  } catch {
    return url;
  }
}

const BETBY_HOST = /^(?:www\.)?(?:jetton\.games|riobet\.com|duel\.com|rollbit\.com)$/i;
const BETBY_PATH = /^\/(?:sports|sportsbook)(\/[^?#]+-\d+)\/?$/i;

/** Адрес матча Betby-родни → тот же матч на bc.game (`/sports/<sport>/…/<slug>-<id>`). */
export function bcgameFromBetby(raw: string): string | null {
  try {
    const url = new URL(raw);
    if (!BETBY_HOST.test(url.hostname)) return null;
    const bt = url.searchParams.get('bt-path') ?? '';
    const tail = /^\/[^?#]+-\d+\/?$/.test(bt) ? bt : BETBY_PATH.exec(url.pathname)?.[1];
    if (tail === undefined) return null;
    return `https://bc.game/sports${tail.replace(/\/+$/, '')}`;
  } catch {
    return null;
  }
}

function sameMatch(a: readonly string[] | undefined, b: readonly string[] | undefined): boolean {
  const key = (names: readonly string[] | undefined) => (names ?? []).map((n) => n.trim().toLowerCase()).sort().join('|');
  return key(a) !== '' && key(a) === key(b);
}

function bcgameFromMate(id: string): string | null {
  const screen = useScreen.getState();
  const fork = screen.forks.get(id) ?? screen.hiddenForks.get(id)?.fork;
  const own = fork?.legs?.find((leg) => leg.s === 'bcgame')?.participants;
  const kin = [...(fork?.legs ?? [])];
  for (const other of screen.forks.values()) {
    if (other !== fork) kin.push(...(other.legs ?? []).filter((leg) => sameMatch(leg.participants, own)));
  }
  for (const mate of kin) {
    if (mate.s === 'bcgame') continue;
    const raw = mate.betUrl ?? mate.url;
    if (typeof raw !== 'string') continue;
    const found = bcgameFromBetby(raw);
    if (found !== null) return found;
  }
  return null;
}

/** Своя метка вкладки. В адрес конторы не кладём — они пишут query в логи. */
export function intentIdOf(id: string, leg: RowLeg): string {
  return `${API_BASE === '/v2' ? 'v2_' : 'v1_'}${intentFor(JSON.stringify([
    id, leg.source, leg.side, leg.period, leg.kind, leg.line, leg.marketKey, leg.sport, leg.eventLine, leg.eventStart,
  ]))}`;
}

/** Обещание и связка вкладки. Навигация идёт по `urlOf`, без этого хвоста. */
export function markOf(id: string, leg: RowLeg): string | null {
  const page = urlOf(id, leg);
  return page === null ? null : withIntent(page, intentIdOf(id, leg));
}

/**
 * Что открывать: событие, если оно есть, иначе корень площадки.
 * «Открыть обе» идёт сюда: нет матча — всё равно вкладка конторы, не отказ.
 */
export function hrefOf(id: string, leg: RowLeg): string | null {
  const door = useAccounts.getState().accounts[leg.source]?.door;
  return urlOf(id, leg) ?? (PRIVATE_MIRROR || /^exp:(live|prematch):/.test(id) || isFeed7Id(id) ? null : doorHome(leg.source, door));
}

/** Уровень входа, по которому человек шёл на самом деле. */
export function precisionOf(id: string, leg: RowLeg): string {
  const page = urlOf(id, leg);
  if (page !== null) {
    if (canonBook(leg.source) === 'thunderpick' && thunderpickEventUrl(page)) return 'event';
    if (canonBook(leg.source) === 'cloudbet' && cloudbetEventUrl(page)) return 'event';
  }
  const warmed = entryLeg(id, leg.source);
  if (warmed !== null && urlFor(leg.source, warmed.url) !== null) return warmed.precision;
  return leg.precision;
}

/**
 * Окно открылось и живое. `null` — не единственный отказ: Chrome с `noopener`
 * в строке features часто отдаёт уже закрытый `Window`, и проверка `=== null`
 * молчит, а вторая контора так и не появилась.
 */
export function opened(win: Window | null): win is Window {
  if (win == null) return false;
  try {
    return win.closed !== true;
  } catch {
    // Чужой origin: ссылка на окно есть, читать `closed` нельзя — значит, пустили.
    return true;
  }
}

/** Контора не должна читать `window.opener` и дойти до сканера. Ручка окна остаётся у нас. */
function sever(win: Window): void {
  try {
    win.opener = null;
  } catch {
    // Чужой документ: обратной ссылки и так нет.
  }
}

/**
 * Второе и дальше — из уже открытого окна конторы, не из сканера.
 * Chrome из одного жеста второе `window.open` сканера кладёт вкладкой сюда.
 *
 * Адрес передаётся сразу. Пустое окно с последующей подменой `location`
 * Фонбет считает подозрительным поведением и ставит свою проверку,
 * хотя тот же адрес, открытый напрямую, показывает матч.
 */
function openVia(
  host: Window | null,
  url: string,
  name: string,
  features?: string,
): Window | null {
  const via = host !== null && opened(host) ? host : window;
  const place = (opener: Window): Window | null => {
    const win = features === undefined ? opener.open(url, name) : opener.open(url, name, features);
    if (!opened(win)) return null;
    sever(win);
    return win;
  };
  try {
    const win = place(via);
    if (win !== null) return win;
  } catch {
    // COOP: чужое окно open больше не зовёт.
  }
  if (via !== window) {
    try {
      const win = features === undefined ? window.open(url, name) : window.open(url, name, features);
      if (opened(win)) {
        sever(win);
        return win;
      }
    } catch { /* Второй путь также может быть заблокирован браузером. */ }
  }
  return null;
}

/**
 * Два окна на половины монитора — рабочий стол, не вкладки сканера.
 *
 * Первая вилка рождает левое и правое окно. Следующая не должна
 * открывать вкладки в Edge, где живёт лента: после COOP конторы
 * `location` и имя окна пропадают, и `window.open` без размера
 * именно так и делает. Совпавшая площадка остаётся в своём окне,
 * вторая половина принимает новую. На узком экране — вкладки.
 */
type Seat = { win: Window; pane: number | null };

const books = new Map<string, Seat>();
/** Полоса без ручки окна: расширение держит окно, страница помнит сторону. */
const heldSlots = new Map<string, number>();

export function resetSlots(): void {
  books.clear();
  heldSlots.clear();
}

export function bookName(source: string): string {
  return feedKey(`seat-${source.replace(/[^a-z0-9_-]+/gi, '')}`);
}

function prune(): void {
  for (const [source, seat] of [...books]) {
    if (!opened(seat.win)) books.delete(source);
  }
}

function seatOf(source: string): Seat | null {
  const seat = books.get(source);
  if (seat === undefined) return null;
  if (!opened(seat.win)) {
    books.delete(source);
    return null;
  }
  return seat;
}

function occupant(pane: number): string | null {
  prune();
  for (const [source, seat] of books) {
    if (seat.pane === pane) return source;
  }
  return null;
}

function rememberedPane(source: string): number | null {
  const seat = seatOf(source);
  if (seat?.pane != null) return seat.pane;
  const held = heldSlots.get(source);
  return held === undefined ? null : held;
}

function holdOn(): boolean {
  return loadLayout().hold;
}

/** Полик: старое окно не переписываем — иначе выигрышная поза уезжает в Sell. */
function freshPoly(source: string): boolean {
  return source === 'polymarket';
}

function seatName(source: string): string {
  return freshPoly(source) ? '_blank' : bookName(source);
}

/** Совпавшая контора держит свою половину; новой отдаём свободную или чужую. */
export function assignPanes(sources: readonly string[]): Map<string, 0 | 1> {
  const out = new Map<string, 0 | 1>();
  const taken = new Set<0 | 1>();
  for (const source of sources) {
    const pane = rememberedPane(source);
    if (pane !== 0 && pane !== 1) continue;
    if (taken.has(pane)) continue;
    out.set(source, pane);
    taken.add(pane);
  }
  for (const source of sources) {
    if (out.has(source)) continue;
    const pane: 0 | 1 | null = !taken.has(0) ? 0 : !taken.has(1) ? 1 : null;
    if (pane === null) continue;
    out.set(source, pane);
    taken.add(pane);
  }
  return out;
}

/** Три ноги — три полосы. Две ноги зовут `assignPanes`, чтобы тесты и расклад не разъехались. */
export function assignSlots(sources: readonly string[], count: number): Map<string, number> {
  if (count <= 2) return assignPanes(sources);
  const out = new Map<string, number>();
  const taken = new Set<number>();
  for (const source of sources) {
    const pane = rememberedPane(source);
    if (pane == null || pane < 0 || pane >= count) continue;
    if (taken.has(pane)) continue;
    out.set(source, pane);
    taken.add(pane);
  }
  for (const source of sources) {
    if (out.has(source)) continue;
    let pane: number | null = null;
    for (let i = 0; i < count; i++) {
      if (!taken.has(i)) {
        pane = i;
        break;
      }
    }
    if (pane === null) continue;
    out.set(source, pane);
    taken.add(pane);
  }
  return out;
}

function keep(source: string, win: Window, pane: number | null): void {
  books.set(source, { win, pane });
}

function refinePinnacle(win: Window, url: string): void {
  if (!isPinnacleStorefront(url)) return;
  void resolvePinnacleDoorUrl(url).then((good) => {
    if (good != null && good !== url && opened(win)) retarget(win, good);
  });
}

function retarget(win: Window, url: string): boolean {
  try {
    win.location.href = url;
    try {
      win.focus();
    } catch {
      // Чужой origin: адрес сменили, фокус иногда режут.
    }
    return true;
  } catch {
    return false;
  }
}

/**
 * Вкладка, не всплывающее окно.
 *
 * Без конторы — одноразовая `_blank` (проба всплывающих на первом заходе).
 * С конторой — та же вкладка, что уже открыта у этой площадки. Features
 * не передаём: Chrome режет второе окно из одного клика. `opener` обнуляет
 * `openVia` у каждого окна, не строкой features.
 */
export function openTab(url: string, source?: string, host: Window | null = null): Window | null {
  const hold = holdOn();
  if (hold && source !== undefined && !freshPoly(source)) {
    const seat = seatOf(source);
    if (seat !== null && retarget(seat.win, url)) {
      refinePinnacle(seat.win, url);
      return seat.win;
    }
    if (seat?.pane != null) {
      const desk = deskOf(window.screen);
      const pane = splitPane(desk, seat.pane, 2);
      if (pane !== null) {
        const again = openVia(host, url, seatName(source), paneFeatures(pane));
        if (opened(again)) {
          keep(source, again, seat.pane);
          refinePinnacle(again, url);
          return again;
        }
      }
    }
  }
  const prevPane = source === undefined ? null : (seatOf(source)?.pane ?? null);
  const name = !hold || source === undefined ? '_blank' : seatName(source);
  const win = openVia(host, url, name);
  if (!opened(win)) return null;
  if (source !== undefined) {
    keep(source, win, prevPane);
    refinePinnacle(win, url);
    return win;
  }
  try {
    win.opener = null;
  } catch {
    // Чужой origin — ссылки на нас и так нет.
  }
  return win;
}

/** Рабочий стол, на котором лежит вкладка сканера — не сумма всех мониторов. */
export type Desk = {
  availLeft: number;
  availTop: number;
  availWidth: number;
  availHeight: number;
};

/** Уже половины не хватает, чтобы две формы ставки стояли рядом. */
const MIN_PANE = 520;
/** Стопка сверху-снизу: ниже форма ставки не читается. */
const MIN_STACK = 360;
/** Третье окно — карточка вилки, не контора. */
const CALC_W = 720;
const CALC_H = 640;
/** Колонка вилки справа, как у открытия из бота. Ширина та же, рост — весь стол. */
const FORK_COL_W = 380;

/** DOM `Screen` не знает `availLeft`/`availTop` — это поле живого Chrome. */
export type ScreenBox = {
  availLeft?: number;
  availTop?: number;
  availWidth?: number;
  availHeight?: number;
  width?: number;
  height?: number;
};

export function deskOf(screen: ScreenBox): Desk {
  return {
    availLeft: screen.availLeft || 0,
    availTop: screen.availTop || 0,
    availWidth: screen.availWidth || screen.width || 0,
    availHeight: screen.availHeight || screen.height || 0,
  };
}

/**
 * Полоса монитора под одну контору. `null` — места нет, остаёмся во вкладках.
 * Последняя полоса забирает остаток, чтобы не было щели в пиксель.
 * `stack` — сверху и снизу, как у THEFORKS; `side` — слева и справа (дефолт).
 */
export function splitPane(
  desk: Desk,
  index: number,
  count: number,
  axis: SplitAxis = 'side',
): Desk | null {
  if (count < 2 || index < 0 || index >= count) return null;
  if (axis === 'stack') {
    if (desk.availHeight < MIN_STACK * count) return null;
    const height = Math.floor(desk.availHeight / count);
    const top = desk.availTop + height * index;
    const pane = index === count - 1 ? desk.availHeight - height * (count - 1) : height;
    return {
      availLeft: desk.availLeft,
      availTop: top,
      availWidth: desk.availWidth,
      availHeight: pane,
    };
  }
  if (desk.availWidth < MIN_PANE * count) return null;
  const width = Math.floor(desk.availWidth / count);
  const left = desk.availLeft + width * index;
  const pane = index === count - 1 ? desk.availWidth - width * (count - 1) : width;
  return {
    availLeft: left,
    availTop: desk.availTop,
    availWidth: pane,
    availHeight: desk.availHeight,
  };
}

/** Карточка вилки поверх двух контор: по центру, не вместо половины. */
export function calcPane(desk: Desk): Desk {
  const width = Math.min(CALC_W, desk.availWidth);
  const height = Math.min(CALC_H, desk.availHeight);
  return {
    availLeft: desk.availLeft + Math.round((desk.availWidth - width) / 2),
    availTop: desk.availTop + Math.round((desk.availHeight - height) / 2),
    availWidth: width,
    availHeight: height,
  };
}

/** Вилка справа от двух контор. `null` — на узком столе колонке нет места. */
export function forkColumn(desk: Desk): Desk | null {
  if (desk.availWidth < MIN_PANE * 2 + FORK_COL_W || desk.availHeight < MIN_STACK) return null;
  return {
    availLeft: desk.availLeft + desk.availWidth - FORK_COL_W,
    availTop: desk.availTop,
    availWidth: FORK_COL_W,
    availHeight: desk.availHeight,
  };
}

export function paneFeatures(pane: Desk): string {
  return `popup=yes,width=${Math.round(pane.availWidth)},height=${Math.round(pane.availHeight)},left=${Math.round(pane.availLeft)},top=${Math.round(pane.availTop)}`;
}

/**
 * Полосы только там, где и монитор, и окно сканера широкие.
 * `screen.availWidth` на телефоне иногда врёт (режим «компьютер», планшет
 * лёжа): без `innerWidth` половины открываются там, где нужны вкладки.
 */
export function splitAllowed(
  desk: Desk,
  count: number,
  view: number,
  axis: SplitAxis = 'side',
  viewH: number = view,
): boolean {
  if (axis === 'stack') {
    return splitPane(desk, 0, count, 'stack') !== null && viewH >= MIN_STACK * count;
  }
  return splitPane(desk, 0, count, 'side') !== null && view >= MIN_PANE * count;
}

/**
 * Что человек выбрал в студии, и что экран реально пустит.
 * `auto` + `side` — прежнее правило: широкий стол делит, узкий остаётся во вкладках.
 */
export function resolveSplit(
  desk: Desk,
  count: number,
  prefs: DeskLayout,
  viewW: number,
  viewH: number,
): { split: boolean; axis: SplitAxis } {
  if (prefs.mode === 'tabs' || count < 2) return { split: false, axis: prefs.axis };
  const prefer = prefs.axis;
  const other: SplitAxis = prefer === 'stack' ? 'side' : 'stack';
  if (prefs.mode === 'auto' && prefer === 'side') {
    return { split: splitAllowed(desk, count, viewW), axis: 'side' };
  }
  if (splitAllowed(desk, count, viewW, prefer, viewH)) return { split: true, axis: prefer };
  if (prefs.mode === 'split' && splitAllowed(desk, count, viewW, other, viewH)) {
    return { split: true, axis: other };
  }
  if (prefs.mode === 'auto') {
    return { split: splitAllowed(desk, count, viewW, 'side', viewH), axis: 'side' };
  }
  return { split: false, axis: prefer };
}

/**
 * Поставить контору в половину стола. Сначала тот же документ, потом
 * окно, которое уже стоит на этой половине, и только затем новое
 * `window.open` С размером — иначе Edge кладёт вкладку в сканер.
 * Второе рождение — из первого окна: Chrome иначе вкладка в FORQ.
 */
export function placeInPane(
  url: string,
  source: string,
  paneIndex: number,
  desk: Desk,
  host: Window | null = null,
  axis: SplitAxis = 'side',
  count = 2,
): Window | null {
  const pane = splitPane(desk, paneIndex, count, axis);
  if (pane === null) return openTab(url, source, host);

  if (holdOn()) {
    const mine = seatOf(source);
    if (mine !== null && !freshPoly(source) && retarget(mine.win, url)) {
      const prev = occupant(paneIndex);
      if (prev !== null && prev !== source) books.delete(prev);
      keep(source, mine.win, paneIndex);
      refinePinnacle(mine.win, url);
      return mine.win;
    }

    const prev = occupant(paneIndex);
    if (prev !== null && prev !== source) {
      if (freshPoly(prev) || freshPoly(source)) {
        books.delete(prev);
      } else {
        const old = seatOf(prev);
        if (old !== null && retarget(old.win, url)) {
          books.delete(prev);
          keep(source, old.win, paneIndex);
          refinePinnacle(old.win, url);
          return old.win;
        }
        books.delete(prev);
      }
    }
  }

  const name = holdOn() ? seatName(source) : '_blank';
  const win = openVia(host, url, name, paneFeatures(pane));
  if (!opened(win)) return null;
  keep(source, win, paneIndex);
  refinePinnacle(win, url);
  return win;
}

/**
 * Полоса монитора под эту контору. Features только при рождении окна.
 */
export function openPane(
  url: string,
  source: string,
  pane: Desk,
  paneIndex: number,
  host: Window | null = null,
): Window | null {
  const seat = seatOf(source);
  if (seat !== null && !freshPoly(source) && retarget(seat.win, url)) {
    refinePinnacle(seat.win, url);
    return seat.win;
  }
  const win = openVia(host, url, seatName(source), paneFeatures(pane));
  if (!opened(win)) return null;
  keep(source, win, paneIndex);
  refinePinnacle(win, url);
  return win;
}

function ticketBits(leg: RowLeg): { cents?: string; limitPrice?: string; shares?: string } {
  if (leg.cents == null || !(leg.cents > 0)) return {};
  if (leg.shares == null || !(leg.shares > 0)) return {};
  if (leg.source === 'predictfun') {
    const price = new Decimal(leg.cents).div(100);
    const shares = new Decimal(leg.shares).toDecimalPlaces(4, Decimal.ROUND_DOWN);
    if (!price.isFinite() || price.lte(0) || price.gte(1) || price.decimalPlaces() > 6 || !shares.isFinite() || shares.lte(0)) return {};
    return { cents: price.times(100).ceil().toFixed(0), limitPrice: price.toFixed(), shares: shares.toFixed() };
  }
  if (leg.source === 'polymarket' && leg.tick != null && leg.tick > 0) {
    const tick = new Decimal(leg.tick);
    const price = new Decimal(leg.cents).div(100).div(tick).floor().times(tick);
    const shares = new Decimal(leg.shares).toDecimalPlaces(4, Decimal.ROUND_DOWN);
    if (price.isFinite() && price.gt(0) && price.lt(1) && price.decimalPlaces() <= 6 && shares.isFinite() && shares.gt(0)) {
      return { cents: price.times(100).ceil().toFixed(0), limitPrice: price.toFixed(), shares: shares.toFixed() };
    }
    return {};
  }
  const cents = new Decimal(leg.cents).toDecimalPlaces(0, Decimal.ROUND_CEIL);
  const shares = new Decimal(leg.shares).toDecimalPlaces(1, Decimal.ROUND_DOWN);
  if (!cents.isFinite() || !shares.isFinite() || cents.lte(0) || shares.lte(0)) return {};
  return { cents: cents.toFixed(0), shares: shares.toFixed(1) };
}

function marketPromise(leg: RowLeg): { order: 'market'; marketUsd: string; dead?: string } | Record<string, never> {
  const usd = marketStake(leg);
  if (usd === null) return {};
  return {
    order: 'market',
    marketUsd: usd,
    ...(typeof leg.marketDead === 'string' ? { dead: leg.marketDead } : {}),
  };
}

/** Kalshi's native market order takes dollar cost, not contract count. */
function promisedAmount(leg: RowLeg): string | undefined {
  if (leg.source !== 'kalshi') return forForm(leg);
  const usd = leg.marketUsd ?? leg.usd;
  if (usd === null || !Number.isFinite(usd) || !(usd > 0)) return undefined;
  return new Decimal(usd).toFixed(2);
}

function promised(leg: RowLeg, url: string) {
  const guest = guestCupis(leg.source);
  const window = STILL_LIVE_BY_SOURCE[leg.source === 'bingoboom' ? 'betboom' : leg.source] ?? 45;
  const intent = intentOf(url);
  const amount = promisedAmount(leg);
  return {
    venue: leg.source,
    url: withoutIntent(url),
    ...(typeof intent === 'string' && intent ? { intent } : {}),
    odds: leg.odds,
    side: leg.side,
    // Имена едут в написании этой же площадки: по ним прицел на чужой странице
    // найдёт строку матча среди сорока соседних.
    who: leg.who,
    stake: formStake(leg, fxOf(), metaOf()),
    ...(amount !== undefined ? { amount } : {}),
    currency: leg.unit !== 'share' && (isRubStake(leg, metaOf()) || !(fxOf() > 0)) ? 'RUB' : 'USD',
    at: Date.now(),
    ...(leg.name ? { name: leg.name } : {}),
    ...(leg.sel ? { sel: leg.sel } : {}),
    ...(leg.period ? { period: leg.period } : {}),
    ...(leg.kind ? { kind: leg.kind } : {}),
    ...(leg.line ? { line: leg.line } : {}),
    ...(leg.marketKey ? { marketKey: leg.marketKey } : {}),
    ...(leg.sport ? { sport: leg.sport } : {}),
    ...(leg.eventLine ? { eventLine: leg.eventLine } : {}),
    ...(leg.eventStart ? { eventStart: leg.eventStart } : {}),
    ...ticketBits(leg),
    ...marketPromise(leg),
    ...(guest
      ? {
          prepareOnly: true as const,
          priceCheck: false as const,
          expiresAt: Date.now() + stillDeadSeconds(window) * 1000,
        }
      : {}),
    ...(loadLayout().fillStake === false ? { fillStake: false as const } : {}),
  };
}

function announce(leg: RowLeg, url: string): void {
  promise(promised(leg, url));
}

/**
 * Попап-блокер закрыл `window.open`. Расширение откроет те же URL вкладками.
 * Старая карточка сообщение не слышит — тогда остаётся красная строка.
 */
/** Карточка с мостиком 0.4.24 умеет `chrome.tabs.create`. Старую не зовём. */
function extOpensTabs(version: string | null): boolean {
  if (version === null || version === '') return false;
  const have = version.split('.').map((n) => Number.parseInt(n, 10) || 0);
  const need = [0, 4, 24];
  const len = Math.max(have.length, need.length);
  for (let i = 0; i < len; i += 1) {
    const a = have[i] ?? 0;
    const b = need[i] ?? 0;
    if (a > b) return true;
    if (a < b) return false;
  }
  return true;
}

function askExtOpen(urls: readonly string[]): boolean {
  if (typeof document === 'undefined' || typeof window === 'undefined') return false;
  if (!extOpensTabs(document.documentElement.getAttribute('data-forq-ext'))) return false;
  try {
    window.postMessage(openTabsEnvelope(urls), window.location.origin);
    return true;
  } catch {
    return false;
  }
}

function postReady(ready: readonly { leg: RowLeg; url: string }[]): void {
  for (const { leg, url } of ready) {
    void promiseOnServer(promised(leg, url));
  }
}

/**
 * Обновить обещание без открытия вкладок. Кнопка проверки читает страницы
 * по свежему кэфу; старое обещание подтвердило бы вчерашнюю цену.
 */
export function refreshPromises(id: string, legs: readonly RowLeg[]): void {
  if (needsIntentUpdate()) return;
  for (const leg of legs) {
    const url = markOf(id, leg);
    if (url === null) continue;
    const one = promised(leg, url);
    promise(one);
    void promiseOnServer(one);
  }
}

function sameOpen(here: string, dest: string, source: string): boolean {
  if (intentOf(here) !== intentOf(dest)) return false;
  if (source === 'predictfun') return sameEvent(here, dest);
  try {
    const a = new URL(here);
    const b = new URL(dest);
    return a.origin === b.origin && a.pathname.replace(/\/+$/, '') === b.pathname.replace(/\/+$/, '');
  } catch {
    return false;
  }
}

/**
 * Уже открытое окно — на адрес этой вилки. Новых вкладок нет.
 * На том же событии не перезагружаем: сотрёт заполненную кассу.
 * Hold обещания сюда не ходит: каждые 45 с это был бы снос формы.
 */
export function retargetOpenBooks(id: string, legs: readonly RowLeg[]): void {
  for (const leg of legs) {
    const url = urlOf(id, leg);
    if (url === null) continue;
    if (freshPoly(leg.source)) continue;
    const seat = seatOf(leg.source);
    if (seat === null) continue;
    try {
      const here = seat.win.location.href;
      if (here !== '' && sameOpen(here, url, leg.source)) continue;
    } catch {
      // COOP: адрес не читается — на проверке всё равно гоним.
    }
    retarget(seat.win, url);
  }
}

/**
 * Пока карточка открыта, обещание не должно протухнуть: полоса иначе пишет
 * «ждём вилку с ленты», хотя человек уже на странице матча.
 */
export const HOLD_PROMISE_MS = 45_000;
/** Пауза после последнего символа: иначе в купон уедут 8, 80, 800. */
export const STAKE_PUSH_MS = 300;

const heldStake = new Map<string, number>();
const heldLegs = new Map<string, readonly RowLeg[]>();
const pushTimers = new Map<string, number>();
const pushRevs = new Map<string, number>();
const lastPushLock = new Map<string, NonNullable<ReturnType<typeof useScreen.getState>['stakeLock']>>();

export function resetStakePush(): void {
  for (const timer of pushTimers.values()) window.clearTimeout(timer);
  pushTimers.clear();
  heldStake.clear();
  heldLegs.clear();
  pushRevs.clear();
  lastPushLock.clear();
}

export function holdPromises(id: string, legs: readonly RowLeg[]): () => void {
  heldStake.set(id, (heldStake.get(id) ?? 0) + 1);
  heldLegs.set(id, legs);
  refreshPromises(id, legs);
  const tick = window.setInterval(() => refreshPromises(id, legs), HOLD_PROMISE_MS);
  return () => {
    window.clearInterval(tick);
    const n = (heldStake.get(id) ?? 1) - 1;
    if (n <= 0) {
      heldStake.delete(id);
      heldLegs.delete(id);
    } else heldStake.set(id, n);
  };
}

/** «Убрать» — обещание с полки больше не пишет и не жмёт купон. */
export function dropHeldPromises(id: string): void {
  const legs = heldLegs.get(id);
  if (legs === undefined) return;
  const urls: string[] = [];
  for (const leg of legs) {
    const page = urlOf(id, leg);
    if (page !== null) urls.push(withoutIntent(page));
    const marked = markOf(id, leg);
    if (marked !== null) urls.push(marked);
  }
  revokePromises(urls);
}

/** Касса ноги открыта нашей кнопкой: обещание есть, купон ждёт сумму. */
export function openedByUs(id: string): boolean {
  if ((heldStake.get(id) ?? 0) > 0) return true;
  for (const [key, attempt] of Object.entries(useBookOpening.getState().by)) {
    if (attempt.state !== 'sent') continue;
    try {
      if ((JSON.parse(key) as unknown[])[0] === id) return true;
    } catch {
      /* чужой ключ */
    }
  }
  return false;
}

export function scheduleStakePush(id: string): void {
  if (typeof window === 'undefined') return;
  if (!openedByUs(id)) return;
  const prev = pushTimers.get(id);
  if (prev !== undefined) window.clearTimeout(prev);
  pushTimers.set(
    id,
    window.setTimeout(() => {
      pushTimers.delete(id);
      flushStakePush(id);
    }, STAKE_PUSH_MS),
  );
}

function currentPriced(id: string): ReturnType<typeof buildRow> {
  const screen = useScreen.getState();
  const settings = useSettings.getState();
  const raw = screen.forks.get(id) ?? screen.hiddenForks.get(id)?.fork;
  if (raw === undefined) return null;
  const locked = new Set(screen.oddsLock?.forkId === id ? Object.keys(screen.oddsLock.by) : []);
  const fork = paintLiveAsks(raw, null, locked);
  return buildRow(fork, {
    bank: settings.bank,
    fx: settings.fx,
    ...capsInput(settings),
    tempo: tempoOf(settings.attempts),
    roundStep: settings.roundStep,
    roundDir: settings.roundDir,
    roundWho: settings.roundWho,
    lock: screen.stakeLock ?? lastPushLock.get(id) ?? undefined,
    odds: screen.oddsLock ?? undefined,
    fees: screen.feeLock ?? undefined,
    curs: screen.curLock ?? undefined,
    free: screen.freeLock ?? undefined,
    hand: screen.handLock ?? undefined,
  });
}

function currentPushLegs(id: string): readonly RowLeg[] | null {
  const priced = currentPriced(id);
  if (priced !== null) return holdLegs(priced.legs);
  return heldLegs.get(id) ?? null;
}

function flushStakePush(id: string): void {
  if (needsIntentUpdate()) return;
  if (!openedByUs(id)) return;
  const legs = currentPushLegs(id);
  if (legs === null) return;
  const items = [];
  for (const leg of legs) {
    const url = markOf(id, leg);
    if (url === null) continue;
    const one = promised(leg, url);
    if (one.amount === undefined && one.shares === undefined) continue;
    items.push({
      venue: one.venue,
      url: one.url,
      ...(one.intent ? { intent: one.intent } : {}),
      ...(one.amount !== undefined ? { amount: one.amount } : {}),
      ...(one.currency !== undefined ? { currency: one.currency } : {}),
      ...(one.shares !== undefined ? { shares: one.shares } : {}),
      ...(one.limitPrice !== undefined ? { limitPrice: one.limitPrice } : {}),
      ...(one.cents !== undefined ? { cents: one.cents } : {}),
      ...('order' in one ? { order: one.order, marketUsd: one.marketUsd, dead: one.dead } : {}),
      ...(one.fillStake === false ? { fillStake: false as const } : {}),
      ...(one.prepareOnly === true ? { prepareOnly: true as const } : {}),
    });
  }
  if (items.length === 0) return;
  const rev = (pushRevs.get(id) ?? 0) + 1;
  pushRevs.set(id, rev);
  stakePush({
    channel: STAKE_PUSH,
    forkId: id,
    rev,
    at: Date.now(),
    legs: items,
  });
  const priced = currentPriced(id);
  if (priced !== null) void rememberFeedPlan(priced, useScreen.getState().meta);
}

if (typeof window !== 'undefined') {
  let prevLock = useScreen.getState().stakeLock;
  useScreen.subscribe((state) => {
    if (state.stakeLock === prevLock) return;
    prevLock = state.stakeLock;
    if (state.stakeLock) {
      lastPushLock.set(state.stakeLock.forkId, state.stakeLock);
      scheduleStakePush(state.stakeLock.forkId);
    }
  });
}

/**
 * Клик по марке: та же вкладка, но с обещанием. Иначе полоса не знает вилку.
 */
export function openBook(id: string, leg: RowLeg): Window | null {
  if (deskOwnsClick()) { openTabs(id, [leg], undefined, {skipFork:true}); return null; }
  const at = Date.now();
  const marked = markOf(id, leg);
  const url = hrefOf(id, leg);
  const note = (state: OpenState): void => useBookOpening.getState().note(id, leg.source, { at, url, state });
  if (needsIntentUpdate()) { note('update'); return null; }
  if (url === null) { note('no-link'); return null; }
  note('opening');
  if (marked !== null) announce(leg, marked);
  const win = openTab(url, leg.source);
  note(win !== null ? 'sent' : !PRIVATE_MIRROR && askExtOpen([marked ?? url]) ? 'sent' : 'blocked');
  if (marked !== null) void promiseOnServer(promised(leg, marked));
  return win;
}

/**
 * Открыть конторы под этими ногами.
 *
 * Широкий стол — два окна. Совпавшая площадка остаётся, вторая половина
 * принимает новую. Вкладка в сканере только если окно режут, или экран
 * узкий. Второе окно зовём из первого: иначе Chrome кладёт его
 * вкладкой в FORQ. `false` — не открылось ничего из нужного, и молчать нельзя.
 */
function openCalc(id: string, host: Window | null, desk: Desk): void {
  openVia(host, forkHref(id), feedKey('forq-fork-calc'), paneFeatures(calcPane(desk)));
}

/** Карточка вилки колонкой справа. Окно рождается сразу: отложенный open браузер режет. */
function openForkColumn(id: string, host: Window | null, column: Desk): void {
  // '' а не about:blank: живое окно той же вилки не уходит в пустой документ
  // поверх уже нарисованной карточки.
  const win = openVia(host, '', feedKey(`forq-fork-${id}`), paneFeatures(column));
  if (!opened(win)) return;
  try {
    win.resizeTo(column.availWidth, column.availHeight);
    win.moveTo(column.availLeft, column.availTop);
  } catch {
    // Чужое окно не двигается. Размер уже задан features при рождении.
  }
  paintFork(id, win);
}

/** Узкое окно по адресу (карточка колла) колонкой справа, в том же жесте, что и кассы. */
function openUrlColumn(url: string, host: Window | null, column: Desk | null): void {
  const win = column === null
    ? openVia(host, url, feedKey('forq-call-panel'))
    : openVia(host, url, feedKey('forq-call-panel'), paneFeatures(column));
  if (!opened(win) || column === null) return;
  try {
    win.resizeTo(column.availWidth, column.availHeight);
    win.moveTo(column.availLeft, column.availTop);
  } catch {
    // Чужое окно не двигается. Размер уже задан features при рождении.
  }
}

/** Вкладки: вилка соседней вкладкой, без размера. Окно — только когда конторы сами окна. */
function openForkTab(id: string, host: Window | null): void {
  const win = openVia(host, '', feedKey(`forq-fork-${id}`));
  if (!opened(win)) return;
  paintFork(id, win);
}

function paintFork(id: string, win: Window): void {
  void import('./pop').then(({ placeCard }) => placeCard(id, win)).catch(() => undefined);
}

/** Не плодить второе окно вилки: мы уже на /call и сами встанем в колонку. */
export type OpenTabsOpts = {
  skipFork?: boolean;
  allowExt?: boolean;
  /** false — без жеста (Telegram ?work=1): молчит расширение — не звать window.open. */
  pageFallback?: boolean;
  onPlaced?: (ok: boolean) => void;
  /** Колл: справа узкое окно этого адреса, не карточка ленты. Всегда окна, не вкладки. */
  forkUrl?: string;
};

/** Текущее окно — колонка вилки справа, кассы занимают остальной стол. */
export function dockForkColumn(): boolean {
  const column = forkColumn(deskOf(window.screen));
  if (column === null) return false;
  try {
    window.resizeTo(column.availWidth, column.availHeight);
    window.moveTo(column.availLeft, column.availTop);
    return true;
  } catch {
    return false;
  }
}

export function openTabs(id: string, legs: readonly RowLeg[], priced?: SightPriced, opts?: OpenTabsOpts): boolean {
  const at = Date.now();
  const note = (leg: RowLeg, state: OpenState, detail?: string): void =>
    useBookOpening.getState().note(id, leg.source, { at, url: urlOf(id, leg), state, ...(detail ? { detail } : {}) });
  if (needsIntentUpdate()) { legs.forEach(leg => note(leg, 'update')); return false; }
  const ready: { leg: RowLeg; url: string; mark: string }[] = [];
  for (const leg of legs) {
    const event = urlOf(id, leg);
    const url = hrefOf(id, leg);
    if (url === null) { note(leg, 'no-link'); continue; }
    note(leg, 'opening');
    const mark = markOf(id, leg) ?? url;
    ready.push({ leg, url, mark });
    if (event !== null) announce(leg, mark);
  }
  if (ready.length === 0) return false;
  const prefs = loadLayout();
  const forkUrl = opts?.skipFork === true ? undefined : opts?.forkUrl;
  const layout = forkUrl !== undefined
    ? { ...prefs, mode: 'split' as const, axis: 'side' as const, withFork: true }
    : opts?.skipFork === true
      ? {
          ...prefs,
          withFork: false,
          ...(prefs.mode === 'tabs' ? {} : { mode: 'split' as const, axis: 'side' as const }),
        }
      : prefs;
  const desk = deskOf(window.screen);
  const viewW = window.innerWidth;
  const viewH = window.innerHeight;
  const count = Math.min(Math.max(ready.length, 0), 3);
  const preview = resolveSplit(desk, count, layout, viewW, viewH);
  const reserveFork = ready.length === 2 && opts?.skipFork !== true && layout.withFork;
  const wantFork = layout.withFork && ready.length === 2 && opts?.skipFork !== true;
  const column = reserveFork && viewW >= MIN_PANE * 2 + FORK_COL_W ? forkColumn(desk) : null;
  // Полосу справа забираем только у окон. Вкладки остаются вкладками браузера.
  const reserve = column !== null && preview.split;
  const bookDesk = reserve ? { ...desk, availWidth: desk.availWidth - column.availWidth } : desk;
  const { split, axis } = reserve ? resolveSplit(bookDesk, count, layout, viewW, viewH) : preview;
  if (deskOwnsClick()) {
    if (ready.length !== legs.length) { legs.forEach(leg=>note(leg,'desk-error','Не у всех сторон есть ссылка. Открытие остановлено.')); return false; }
    return openOnDesk(id, legs, ready, note, priced, {
      desk,
      axis,
      count,
      column: reserveFork ? forkColumn(desk) : null,
      calc: !wantFork && opts?.skipFork !== true && layout.calc === 'pop' && ready.length >= 2,
      forkUrl,
    });
  }
  const openOnPage = (): boolean => {
    let got = 0;
    let host: Window | null = null;
    const missed: string[] = [];
    if (split) {
      const panes = assignSlots(
        ready.map(({ leg }) => leg.source),
        count,
      );
      for (const { leg, url, mark } of ready) {
        const pane = panes.get(leg.source);
        let win: Window | null = null;
        if (pane !== undefined) win = placeInPane(url, leg.source, pane, bookDesk, host, axis, count);
        if (win === null) {
          const extra = splitPane(bookDesk, Math.max(count - 1, 1), count, axis);
          if (extra !== null) win = openPane(url, leg.source, extra, Math.max(count - 1, 1), host);
        }
        if (win === null) win = openTab(url, leg.source, host);
        if (win !== null) {
          got += 1;
          if (host === null) host = win;
        } else {
          missed.push(mark);
        }
      }
    } else {
      for (const { leg, url, mark } of ready) {
        const win = openTab(url, leg.source, host);
        if (win !== null) {
          got += 1;
          if (host === null) host = win;
        } else {
          missed.push(mark);
        }
      }
    }
    // CUPIS reports blocked windows honestly; fire-and-forget extension posts are not acknowledgements.
    const sentToExtension = !PRIVATE_MIRROR && missed.length > 0 && askExtOpen(missed);
    if (sentToExtension) got += missed.length;
    ready.forEach(({ leg, url, mark }) => note(leg, (missed.includes(url) || missed.includes(mark)) && !sentToExtension ? 'blocked' : 'sent'));
    if (wantFork && forkUrl !== undefined) {
      openUrlColumn(forkUrl, host, column ?? forkColumn(desk));
    } else if (wantFork) {
      if (reserve && column !== null) openForkColumn(id, host, column);
      else openForkTab(id, host);
    }
    if (!PRIVATE_MIRROR && !RU_FEED && layout.mode === 'tabs' && ready.length >= 2 && opts?.skipFork !== true) {
      // Узкий калькулятор поверх вкладок, не третье окно на половину стола.
      void import('./pop').then(({ popCalc }) => popCalc(id)).catch(() => undefined);
    } else if (!wantFork && layout.calc === 'pop' && ready.length >= 2 && opts?.skipFork !== true) {
      openCalc(id, host, desk);
    }
    const opened = PRIVATE_MIRROR ? ready.filter(({ mark }) => !missed.includes(mark)) : ready;
    postReady(opened.filter(({ leg }) => urlOf(id, leg) !== null).map(({ leg, mark }) => ({ leg, url: mark })));
    if (opened.length > 0) {
      remember(
        id,
        opened.map(({ url }) => url),
        {
          priced,
          openedLegs: legs.flatMap((leg, index) => (opened.some(item => item.leg === leg) ? [index] : [])),
        },
      );
    }
    return got === (PRIVATE_MIRROR || RU_FEED ? legs.length : ready.length);
  };
  // /call без allowExt — окна страницы, жест ещё жив. allowExt — стол
  // расширения: два normal-окна касс, без панели вилки. ?work=1 без жеста:
  // pageFallback=false, молчит стол — кнопка на карточке.
  if (opts?.skipFork === true && opts.allowExt !== true) {
    const ok = openOnPage();
    opts.onPlaced?.(ok);
    return ok;
  }
  // Стол расширения — окна. «Во вкладках» не перебиваем: иначе живой архив
  // всегда зовёт chrome.windows (25.09 чинили, потом снова сломали).
  if (layout.mode === 'tabs') {
    const ok = openOnPage();
    opts?.onPlaced?.(ok);
    return ok;
  }
  // CUPIS does not depend on the shared window extension: open synchronously
  // while the user's click activation is still available.
  if (PRIVATE_MIRROR || isCupisForkId(id)) {
    const ok = openOnPage();
    opts?.onPlaced?.(ok);
    return ok;
  }
  // Живое расширение раскладывает окна само (normal-окна: вход Google жив,
  // те же слоты на повтор). Молчит дольше PANES_WAIT_MS — окна открывает
  // страница: активация жеста живёт ~5 с, запасной путь в неё укладывается.
  if (extAwake()) {
    const book = split ? bookDesk : desk;
    const panes: OpenPane[] = ready.map(({ leg, url }, index) => {
      const slot = assignSlots(ready.map((one) => one.leg.source), count).get(leg.source) ?? index;
      const box = splitPane(book, slot, count, axis) ?? book;
      return { url, role: 'book', source: leg.source, left: box.availLeft, top: box.availTop,
        width: box.availWidth, height: box.availHeight };
    });
    const panel = column ?? forkColumn(desk);
    if (wantFork && ready.length === 2 && panel !== null) {
      panes.push({ url: forkUrl ?? panelUrl(id), role: 'fork', source: FORK_PANE, left: panel.availLeft,
        top: panel.availTop, width: panel.availWidth, height: panel.availHeight });
    }
    void askPanes(axis, panes, PANES_WAIT_MS, ready.length).then((placed) => {
      if (!placed) {
        if (opts?.pageFallback === false) {
          opts.onPlaced?.(false);
          return;
        }
        opts?.onPlaced?.(openOnPage());
        return;
      }
      ready.forEach(({ leg }) => note(leg, 'sent'));
      postReady(ready.filter(({ leg }) => urlOf(id, leg) !== null).map(({ leg, mark }) => ({ leg, url: mark })));
      remember(id, ready.map(({ url }) => url), {
        priced,
        openedLegs: legs.flatMap((leg, index) => (hrefOf(id, leg) !== null ? [index] : [])),
      });
      opts?.onPlaced?.((!PRIVATE_MIRROR && !RU_FEED) || ready.length === legs.length);
    });
    return true;
  }
  const ok = openOnPage();
  opts?.onPlaced?.(ok);
  return ok;
}

type ReadyLeg = { leg: RowLeg; url: string; mark: string };
type DeskPlan = {
  desk: Desk;
  axis: SplitAxis;
  count: number;
  column: Desk | null;
  calc: boolean;
  forkUrl?: string;
};

/**
 * Стол AdsPower: кассы — в профилях своих контор (половины монитора сканера),
 * панель вилки — окном здесь, пока жест жив. Ни одного окна Edge под кассу:
 * там домашний IP. Не вышло — красная строка с конторой и профилем.
 */
export function openOnDesk(
  id: string,
  legs: readonly RowLeg[],
  ready: readonly ReadyLeg[],
  note: (leg: RowLeg, state: OpenState, detail?: string) => void,
  priced: SightPriced | undefined,
  plan: DeskPlan,
): boolean {
  const state = useDesk.getState();
  const fail = (detail: string): false => {
    ready.forEach(({ leg }) => note(leg, 'desk-error', detail));
    state.fail(detail);
    return false;
  };
  const version = extAwake() && extPong !== null ? extPong.version : null;
  if (version === null) return fail(ADS_COPY.deskNoExt);
  if (!versionAtLeast(version, DESK_BOOKS_MIN)) return fail(ADS_COPY.oldDesk);
  const meta = metaOf();
  const slots = Math.max(plan.count, 2);
  const sides = deskSlots(state, ready.map(({ leg }) => leg.source));
  const slotOf = (_source: string, index: number): number => sides[index] ?? index;
  const profileOf = (source: string, _index: number): string => state.books[source] || '';

  // Места касс — монитор, где открывали столы, не тот, куда унесли ленту.
  // Нужны только новому окну профиля: живые окна клик не двигает.
  const home = deskArea() ?? plan.desk;
  const homeColumn = plan.column !== null ? forkColumn(home) : null;
  const area = homeColumn !== null
    ? { ...home, availWidth: home.availWidth - homeColumn.availWidth }
    : home;
  const books: BookLeg[] = ready.map(({ leg, url }, index) => {
    const slot = slotOf(leg.source, index);
    const box = splitPane(area, slot, slots, plan.axis) ?? splitPane(area, slot, slots, 'side');
    return {
      source: leg.source,
      url,
      profileId: profileOf(leg.source, index),
      slot,
      ...(state.books[bookKey(leg.source)] || state.books[leg.source] ? { own: true } : {}),
      ...(box !== null
        ? { box: { left: box.availLeft, top: box.availTop, width: box.availWidth, height: box.availHeight } }
        : {}),
    };
  });
  if (plan.column !== null) {
    const panel = deskPanel();
    if (panel !== null && plan.forkUrl !== undefined) retarget(panel, plan.forkUrl);
    else if (panel !== null) paintFork(id, panel);
  } else if (plan.calc) openCalc(id, null, plan.desk);

  void proxyAsk({op:'openBooks',at:Date.now(),requestId:crypto.randomUUID(),books:books.map((book,index)=>({...book,promise:promised(ready[index]!.leg,ready[index]!.mark)}))}).then((reply) => {
    if (reply.panel !== undefined && plan.column !== null) placeDeskPanel(reply.panel);
    const failed = new Map((reply.failed ?? []).map((one: {source:string;error:string}) => [one.source, one.error]));
    const whole = reply.ok || reply.failed !== undefined ? undefined : (reply.error ?? ADS_COPY.pairFailed);
    const good: ReadyLeg[] = [];
    const lines: string[] = [];
    for (const [index, one] of ready.entries()) {
      const error = failed.get(one.leg.source) ?? whole;
      if (error === undefined) {
        note(one.leg, 'sent');
        good.push(one);
        continue;
      }
      const who = `${venueName(meta, one.leg.source)} (${profileName(profileOf(one.leg.source, index))})`;
      note(one.leg, 'desk-error', `${who}: ${error}`);
      lines.push(`${who}: ${error}`);
    }
    if (lines.length > 0) useDesk.getState().fail(lines.join(' · '));
    if (good.length === 0) return;
    postReady(good.filter(({ leg }) => urlOf(id, leg) !== null).map(({ leg, mark }) => ({ leg, url: mark })));
    remember(id, good.map(({ url }) => url), {
      priced,
      openedLegs: legs.flatMap((leg, index) => (good.some((one) => one.leg === leg) ? [index] : [])),
    });
  });
  return true;
}

export const PANES_WAIT_MS = 600;
/** Реле ответило «принял»: три окна с подгонкой размеров идут дольше 600 мс. */
export const PANES_HOLD_MS = 12_000;
const FORK_PANE = 'forq';
const EXT_AWAKE_MS = 30_000;
let extPong: { version: string; at: number } | null = null;

/** Панель вилки — в браузере сканера: куки сессии на месте. */
export function panelUrl(id: string): string {
  const href = new URL(forkHref(id), window.location.origin);
  href.searchParams.set('panel', '1');
  return href.toString();
}

/** Расширение отвечало на пинг последние 30 с и умеет раскладывать окна. */
export function extAwake(now = Date.now()): boolean {
  return extPong !== null && now - extPong.at < EXT_AWAKE_MS && extPlacesPanes(extPong.version);
}

export function noteExtPong(version: string | null, at = Date.now()): void {
  extPong = version === null ? null : { version, at };
}

/** Пока лента открыта — пинг раз в 20 с: служебный процесс не засыпает, версия свежая. */
export function keepExtAwake(where: Window = window): () => void {
  const beat = (): void => {
    void pingExt(where).then((pong) => noteExtPong(pong?.version ?? null));
    void syncProxyDesk();
  };
  beat();
  const timer = setInterval(beat, 20_000);
  return () => clearInterval(timer);
}

/**
 * Стол расширения: `true` — окна открыло оно, `false` — молчит, открываем сами.
 *
 * Принятая просьба ждёт итога: запасной путь поверх уже открытых окон давал
 * 3 кассы и 2 окна вилки (28.09, два монитора). Кассы общим окном — тоже итог.
 */
export function askPanes(
  axis: SplitAxis,
  panes: readonly OpenPane[],
  wait = PANES_WAIT_MS,
  books = panes.filter((pane) => pane.role === 'book').length,
): Promise<boolean> {
  const id = `p${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
  return new Promise((resolve) => {
    const give = (): void => {
      window.removeEventListener('message', hear);
      resolve(false);
    };
    let timer = setTimeout(give, wait);
    function hear(event: MessageEvent): void {
      const held = parseOpenPanesWait(event.data);
      if (held !== null && held.id === id) {
        clearTimeout(timer);
        timer = setTimeout(give, PANES_HOLD_MS);
        return;
      }
      const ok = parseOpenPanesOk(event.data);
      if (ok === null || ok.id !== id) return;
      clearTimeout(timer);
      window.removeEventListener('message', hear);
      resolve(ok.ok && (ok.mode === 'panes' || ok.books >= books));
    }
    window.addEventListener('message', hear);
    window.postMessage(openPanesEnvelope(axis, panes, id), window.location.origin);
  });
}

/**
 * Человек пошёл ставить: контора открыта, сумма в буфере. Ставки ещё НЕТ.
 *
 * Отметка по конторе — единственное, что мы про счета человека знаем сами: по
 * ней считается перекос, из-за которого контора режет счёт (`accounts.ts`).
 */
export function going(row: FeedRow, index: 1 | 2 | 3): void {
  useScreen.getState().openLeg(row.id, index);
  const leg = legsOf(row)[index - 1];
  if (leg !== undefined) useAccounts.getState().noteUse(leg.source);
}

/**
 * Открыть ногу целиком: буфер, вкладки, отметка. `false` — вкладку заблокировали.
 *
 * `pair` первым нажатием открывает все конторы вилки: между первой и второй
 * ногой живёт весь риск этого продукта. Любая следующая кнопка открывает свою:
 * повтор той же URL не нужен, а чужую ногу человек вправе открыть ещё раз.
 */
export function enter(row: FeedRow, index: 1 | 2 | 3, pair: boolean): boolean {
  const legs = legsOf(row);
  const leg = legs[index - 1];
  if (leg === undefined) return true;

  const steps = useScreen.getState().steps[row.id] ?? blankStep();
  const started = steps.opened.some((at) => at !== null);
  const mine = hrefOf(row.id, leg) !== null ? [leg] : [];
  const extras =
    pair && !started
      ? legs.filter((one) => one !== leg && hrefOf(row.id, one) !== null)
      : [];
  const toOpen = [...mine, ...extras];
  // Буфер до вкладок: жест сгорает, как только ушёл фокус. У двери без
  // события — название в поиск; сумму потом берут `Grab` / клавиша «c».
  void copy(bufferOf(row, leg, toOpen));
  const ok = openTabs(row.id, toOpen, pricedOf(row));

  // Отметка — только у нажатой ноги. Первое нажатие в паре уже открыло все
  // вкладки, но вторая кнопка должна остаться входом в свою контору, а не
  // сразу смениться на «поставил».
  if (steps.opened[index - 1] === null) going(row, index);
  return ok;
}

// Телефон рядом, не вместо: одна вкладка, очередь второй ноги.
export { phoneEnter, hrefOfPhone } from './phone/phoneEnter';

/**
 * «Поставил»: деньги на кону. Только отсюда идут счёт дня и замер темпа.
 *
 * На первой ноге запоминается обещание строки: с ним спасатель потом сверяет,
 * ушла ли цена. Текущая прибыль для этого не годится — она пересчитана из тех же
 * подешевевших цен.
 */
export function placed(row: FeedRow, index: 1 | 2 | 3): void {
  const screen = useScreen.getState();
  // The operator marked this displayed stake as placed. Freeze its entered
  // amount and quote before further live updates; this is not broker proof.
  const accepted = legsOf(row)[0];
  if (index === 1 && row.row.legs.length === 2 && accepted && !accepted.lay && !accepted.freebet) {
    const form = accepted.unit === 'share' && accepted.cents !== null
      ? String(accepted.cents) : accepted.odds;
    screen.setOddsQuote(row.id, accepted.source, form);
    screen.setStakeLock({
      forkId: row.id,
      source: accepted.source,
      form: forForm(accepted),
      ...(accepted.unit === 'share' ? { as: 'share' as const } : {}),
    });
  }
  const seconds = screen.confirmLeg(row.id, index, index === 1 ? row.row.profit : undefined);
  const settings = useSettings.getState();
  if (index === 1) {
    // Начатая вилка — в счёт дня: по паре «начал/довёл» видно, сколько попыток
    // рассыпалось между первой и второй ногой.
    settings.noteTried();
    // Бумага отмечается на ПЕРВОЙ ноге: с этого момента начинается гонка, исход
    // которой мы обещали разобрать. Отметка на второй измеряла бы уже
    // состоявшийся результат.
    if (PAPER_UI && usePaper.getState().on) {
      markPaper(row.id, row.row.roiPct, row.row.profit, tempoOf(settings.attempts));
    }
  }
  if (index === 1) void rememberFeedPlan(row.row, screen.meta);
  if (seconds === null) return;
  settings.noteTaken();

  const legs = legsOf(row);
  // Нога без входа делает замер темпа бессмысленным: человек искал контору и
  // матч руками, и сколько это заняло, мы не знаем. Выдуманная надбавка прямо
  // сместила бы ту величину, по которой строка потом объявляется успеваемой.
  if (!legs.every((leg) => urlOf(row.id, leg) !== null)) return;
  // Из замера вычитается дорога до формы ставки: мерить человека по чужой
  // медленной площадке значило бы записать ему в норму её навигацию. Уровень
  // берётся тот, по которому человек шёл: адресация могла привести его сразу на
  // страницу матча, и вычитать за поиск матча тогда не за что.
  const cost = legs.reduce((sum, leg) => sum + navigationCost(precisionOf(row.id, leg)), 0);
  settings.noteAttempt(seconds, cost);
}
